import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2 } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { Biz360Logo } from "../components/layout/Biz360Logo";

const COLORS = {
  principale: '#210875',
  success: '#059669',
  bg: '#FDFCFF',
};

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  const api = useApi();
  const { updateUser } = useAuth();

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setStatus("error");
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await api.get(`/subscription/verify/${sessionId}`);
      if (response.data.status === "success") {
        setStatus("success");
        setSubscription(response.data.subscription);
        // Mettre à jour l'utilisateur local
        updateUser({ subscription_status: "active" });
      } else {
        setStatus("pending");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  const goToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: COLORS.bg }}>
      <Biz360Logo size="lg" />

      <div className="mt-8 text-center max-w-md">
        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto animate-spin" style={{ color: COLORS.principale }} />
            <h1 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
              Vérification du paiement...
            </h1>
            <p className="text-muted-foreground mt-2">Merci de patienter quelques instants</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-20 h-20 mx-auto" style={{ color: COLORS.success }} />
            <h1 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
              Paiement réussi !
            </h1>
            <p className="text-muted-foreground mt-2">
              Votre abonnement <strong>{subscription?.name}</strong> est maintenant actif.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Un email de confirmation vous a été envoyé.
            </p>

            <button
              onClick={goToDashboard}
              className="mt-8 w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: COLORS.principale }}
              data-testid="go-dashboard-btn"
            >
              Accéder à mon tableau de bord
            </button>
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto animate-spin" style={{ color: COLORS.principale }} />
            <h1 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
              Paiement en cours de traitement
            </h1>
            <p className="text-muted-foreground mt-2">
              Votre paiement est en cours de vérification. Vous recevrez un email de confirmation.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold mt-4 text-red-600">
              Erreur de vérification
            </h1>
            <p className="text-muted-foreground mt-2">
              Une erreur est survenue. Veuillez contacter le support.
            </p>
            <a
              href="mailto:t.samblat@gmail.com"
              className="mt-4 inline-block text-primary underline"
            >
              Contacter le support
            </a>
          </>
        )}
      </div>
    </div>
  );
};
